import { BalanceEntity } from '../balance-entity.model';
import { isSpeiStrictService } from '../gestor-servicio';
import { normalizeSearchText } from '../search-text';

/**
 * Entidades SPEI ASIGNADAS de los rieles SIN cuenta padre (products_spei_sin_cuenta_padre).
 * TypeScript puro.
 *
 * Quien no tiene cuenta padre en un riel no tiene a quién pedirle "las empresas del
 * padre": sus empresas, unidades y cuentas YA llegaron en el login, acotadas por banca
 * a sus gestores vigentes (`extra_gestor_entities`). De ahí, y SOLO de ahí, salen las
 * filas: ni `balance_object` (lo reescribe cada elección) ni `father_accounts_list`
 * (son cuentas padre), y ninguna lectura de red de nivel superior.
 */

export type SpeiAssignedLevel = 'company' | 'unit' | 'account';

export interface SpeiAssignedEntry {
  /** `${level}:${identidad}` (R7). Estable para `track` y para resolver el pick (R21). */
  readonly id: string;
  readonly level: SpeiAssignedLevel;
  /** Riel efectivo normalizado (R5). Nunca se pinta (R12, Q3). */
  readonly rail: string;
  readonly name: string;
  readonly clabe: string | null;
  /** Nombre + CLABE normalizados UNA vez (búsqueda, R13). */
  readonly searchKey: string;
  /** Entidad CRUDA de `extra_gestor_entities`: la escribe el activador. La plantilla no la lee. */
  readonly entity: BalanceEntity;
}

/**
 * Forma de la sesión respecto a las cuentas padre de los rieles asignados:
 * - `with-father`: todos los rieles con raíces tienen cuenta padre, o hay una cuenta
 *   padre sin riel (R2): el camino de hoy, sin tocar.
 * - `without-father`: ningún riel asignado tiene cuenta padre y hay raíces.
 * - `mixed`: algún riel con cuenta padre y algún riel sin ella con raíces.
 * - `none`: sin rieles asignados, o sin cuenta padre y sin raíces.
 */
export type SpeiSessionShape = 'with-father' | 'without-father' | 'mixed' | 'none';

export interface SpeiAssignedEntries {
  readonly shape: SpeiSessionShape;
  /** Raíces por riel sin cuenta padre, en orden de rieles asignados (solo rieles con raíces). */
  readonly byRail: ReadonlyMap<string, readonly SpeiAssignedEntry[]>;
  /** Todas, en orden de rieles y de aparición. */
  readonly all: readonly SpeiAssignedEntry[];
}

export interface SpeiAssignedInput {
  /** Inventario de la pantalla (`father_accounts_list` + extras): solo para saber qué riel tiene padre. */
  readonly inventory: readonly BalanceEntity[];
  /** `extra_gestor_entities`, SOLO: la única fuente de raíces (R1). */
  readonly assignedEntities: readonly BalanceEntity[];
  /** Rieles SPEI asignados. */
  readonly speiProviders: readonly string[];
}

const NIVEL_POR_TIPO: ReadonlyMap<string, SpeiAssignedLevel> = new Map([
  ['empresa', 'company'],
  ['unidad de negocio', 'unit'],
  ['cuenta', 'account'],
]);

const ID_DEL_NIVEL: Record<SpeiAssignedLevel, 'id_company' | 'id_business_unit' | 'id_account'> = {
  company: 'id_company',
  unit: 'id_business_unit',
  account: 'id_account',
};

const VACIO: SpeiAssignedEntries = Object.freeze({
  shape: 'none' as const,
  byRail: new Map<string, readonly SpeiAssignedEntry[]>(),
  all: Object.freeze([]) as readonly SpeiAssignedEntry[],
});

/** `Unidad  de Negocio` llega del backend con doble espacio en algunos endpoints. */
function tipoNormalizado(entity: BalanceEntity | null | undefined): string {
  return String(entity?.entity_type ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function rielNormalizado(valor: unknown): string {
  return String(valor ?? '').trim().toUpperCase();
}

function texto(valor: unknown): string {
  return String(valor ?? '').trim();
}

/** Nivel reconocido para una raíz (la `Cta_Padre` no está a propósito). */
export function speiAssignedLevelOf(entity: BalanceEntity | null | undefined): SpeiAssignedLevel | null {
  return NIVEL_POR_TIPO.get(tipoNormalizado(entity)) ?? null;
}

/**
 * Riel efectivo (R5): el que devolvió la API (`api_provider`) si vino; si no, el
 * `provider`. El login pisa `provider` con el del PRIMER gestor de la llave, que puede
 * ser TPV (`MEMPHIS`) aunque la entidad sea de un riel SPEI (caso 7).
 */
export function speiEffectiveRail(entity: BalanceEntity | null | undefined): string {
  const api = rielNormalizado(entity?.api_provider);
  return api || rielNormalizado(entity?.provider);
}

function clabeDe(entity: BalanceEntity): string {
  return texto(entity.clabe) || texto(entity.clabe_account);
}

/**
 * Identidad para no repetir (R7): la de la feature 61, CLABE > id del nivel >
 * `polipay_account`. Sin ninguno ⇒ `null` (no se fusiona con nada).
 */
function identidadDe(entity: BalanceEntity, level: SpeiAssignedLevel): string | null {
  const candidatos = [clabeDe(entity), texto(entity[ID_DEL_NIVEL[level]]), texto(entity.polipay_account)];
  return candidatos.find((valor) => valor.length > 0) ?? null;
}

function nombreDe(entity: BalanceEntity): string {
  return texto(entity.id_entity_name) || texto(entity.name);
}

/** Una pasada por el inventario: ¿hay cuenta padre sin riel (R2)? y rieles con cuenta padre. */
function leerCuentasPadre(
  inventory: readonly BalanceEntity[],
  rieles: ReadonlySet<string>,
): { padreSinRiel: boolean; conPadre: Set<string> } {
  const conPadre = new Set<string>();
  for (const entity of inventory ?? []) {
    if (tipoNormalizado(entity) !== 'cta_padre') continue;
    const riel = rielNormalizado(entity?.provider);
    if (!riel) return { padreSinRiel: true, conPadre };
    if (rieles.has(riel)) conPadre.add(riel);
  }
  return { padreSinRiel: false, conPadre };
}

interface Acumulado {
  readonly companies: SpeiAssignedEntry[];
  readonly units: SpeiAssignedEntry[];
  readonly accounts: SpeiAssignedEntry[];
  readonly companyNames: Set<string>;
  readonly unitNames: Set<string>;
}

/**
 * Raíces asignadas de cada riel sin cuenta padre (R1–R9). O(n): una pasada por el
 * inventario, una por las entidades asignadas y una por las elegibles, con índices
 * `Map`/`Set` (los nombres se normalizan UNA vez por entidad).
 */
export function speiAssignedEntries(input: SpeiAssignedInput): SpeiAssignedEntries {
  const orden: string[] = [];
  const rieles = new Set<string>();
  for (const code of input.speiProviders ?? []) {
    const riel = rielNormalizado(code);
    if (riel && !rieles.has(riel)) {
      rieles.add(riel);
      orden.push(riel);
    }
  }
  // R3: sin rieles asignados no hay raíces.
  if (rieles.size === 0) return VACIO;

  const { padreSinRiel, conPadre } = leerCuentasPadre(input.inventory, rieles);
  // R2: con una cuenta padre sin riel no se sabe qué riel carece de padre: como hoy.
  if (padreSinRiel) return { ...VACIO, shape: 'with-father' };

  const porRiel = new Map<string, Acumulado>();
  for (const riel of orden) {
    if (!conPadre.has(riel)) {
      porRiel.set(riel, { companies: [], units: [], accounts: [], companyNames: new Set(), unitNames: new Set() });
    }
  }

  const vistas = new Set<string>();
  for (const entity of input.assignedEntities ?? []) {
    if (!entity) continue;
    const level = speiAssignedLevelOf(entity);
    if (level === null) continue;
    // R4: solo procedencia SPEI (ausente = SPEI).
    if (!isSpeiStrictService(entity.gestor_service)) continue;
    // R5/R6: riel efectivo asignado y SIN cuenta padre (vacío ⇒ fuera).
    const acumulado = porRiel.get(speiEffectiveRail(entity));
    if (!acumulado) continue;
    // R9: sin CLABE ni cuenta no hay con qué operar.
    const clabe = clabeDe(entity);
    if (!clabe && !texto(entity.polipay_account)) continue;
    // R7: la misma entidad por dos llaves del login se lista una vez.
    const identidad = identidadDe(entity, level);
    if (identidad !== null) {
      const llave = `${level}|${identidad}`;
      if (vistas.has(llave)) continue;
      vistas.add(llave);
    }

    const name = nombreDe(entity);
    const entry: SpeiAssignedEntry = {
      id: `${level}:${identidad ?? `pos:${vistas.size}`}`,
      level,
      rail: speiEffectiveRail(entity),
      name,
      clabe: clabe || null,
      searchKey: normalizeSearchText(`${name} ${clabe}`),
      entity,
    };
    if (level === 'company') {
      acumulado.companies.push(entry);
      if (name) acumulado.companyNames.add(normalizeSearchText(name));
    } else if (level === 'unit') {
      acumulado.units.push(entry);
      if (name) acumulado.unitNames.add(normalizeSearchText(name));
    } else {
      acumulado.accounts.push(entry);
    }
  }

  const byRail = new Map<string, readonly SpeiAssignedEntry[]>();
  const all: SpeiAssignedEntry[] = [];
  for (const [riel, acumulado] of porRiel) {
    const raices = raicesDelRiel(acumulado);
    if (raices.length === 0) continue;
    byRail.set(riel, raices);
    all.push(...raices);
  }

  const shape: SpeiSessionShape =
    conPadre.size === 0
      ? all.length > 0
        ? 'without-father'
        : 'none'
      : all.length > 0
        ? 'mixed'
        : 'with-father';
  return { shape, byRail, all };
}

/**
 * R8 (Q8): una unidad o cuenta que cuelga —por el NOMBRE de su padre, que banca ya
 * manda— de una empresa o unidad elegible del MISMO riel no es raíz: ya se llega a
 * ella bajando desde esa raíz. Si no, es raíz por sí misma (otra rama).
 */
function raicesDelRiel(acumulado: Acumulado): SpeiAssignedEntry[] {
  const cuelgaDeEmpresa = (nombre: unknown): boolean => {
    const normalizado = normalizeSearchText(texto(nombre));
    return !!normalizado && acumulado.companyNames.has(normalizado);
  };
  const cuelgaDeUnidad = (nombre: unknown): boolean => {
    const normalizado = normalizeSearchText(texto(nombre));
    return !!normalizado && acumulado.unitNames.has(normalizado);
  };

  const raices: SpeiAssignedEntry[] = [...acumulado.companies];
  for (const unidad of acumulado.units) {
    if (!cuelgaDeEmpresa(unidad.entity.company)) raices.push(unidad);
  }
  for (const cuenta of acumulado.accounts) {
    if (!cuelgaDeUnidad(cuenta.entity.business_unit) && !cuelgaDeEmpresa(cuenta.entity.company)) {
      raices.push(cuenta);
    }
  }
  return raices;
}

/** Conteos de un conjunto de raíces; `null` = "No disponible", jamás `0` por ausencia. */
export interface SpeiAssignedCounts {
  readonly companies: number | null;
  readonly units: number | null;
  readonly accounts: number | null;
}

/** Solo un entero finito ≥ 0 es dato (sin `Number(x) || 0`). */
function enteroONulo(valor: unknown): number | null {
  return typeof valor === 'number' && Number.isInteger(valor) && valor >= 0 ? valor : null;
}

/**
 * R15: empresas = raíces Empresa; unidades = Σ `business_units_count` de las empresas
 * raíz + raíces Unidad; cuentas = Σ `accounts_count` de empresas y unidades raíz +
 * raíces Cuenta. Un precalculado ausente o inválido deja ESE contador en `null`.
 */
export function speiAssignedCounts(entries: readonly SpeiAssignedEntry[]): SpeiAssignedCounts {
  let companies = 0;
  let units: number | null = 0;
  let accounts: number | null = 0;
  for (const entry of entries ?? []) {
    if (entry.level === 'company') {
      companies++;
      const unidades = enteroONulo(entry.entity.business_units_count);
      units = units === null || unidades === null ? null : units + unidades;
      const cuentas = enteroONulo(entry.entity.accounts_count);
      accounts = accounts === null || cuentas === null ? null : accounts + cuentas;
    } else if (entry.level === 'unit') {
      if (units !== null) units++;
      const cuentas = enteroONulo(entry.entity.accounts_count);
      accounts = accounts === null || cuentas === null ? null : accounts + cuentas;
    } else if (accounts !== null) {
      accounts++;
    }
  }
  return { companies, units, accounts };
}

/**
 * Las raíces cuyo nombre o CLABE contienen el término, sin distinguir mayúsculas ni
 * acentos (R13). Término vacío ⇒ la MISMA lista. Una pasada, O(n).
 */
export function filterSpeiAssignedEntries(
  list: readonly SpeiAssignedEntry[],
  term: string,
): readonly SpeiAssignedEntry[] {
  const buscado = normalizeSearchText(term);
  if (!buscado) return list;
  return list.filter((entry) => entry.searchKey.includes(buscado));
}

/**
 * R16: ¿la sesión no trae NINGUNA entidad SPEI de la que partir? Sin `Cta_Padre` en el
 * inventario y sin Empresa, Unidad o Cuenta de procedencia SPEI (cualquier riel) entre
 * las asignadas. La pantalla lo combina con "sin filas de proveedor ni raíces".
 */
export function speiHasNoEntities(input: Pick<SpeiAssignedInput, 'inventory' | 'assignedEntities'>): boolean {
  for (const entity of input.inventory ?? []) {
    if (tipoNormalizado(entity) === 'cta_padre') return false;
  }
  for (const entity of input.assignedEntities ?? []) {
    if (speiAssignedLevelOf(entity) !== null && isSpeiStrictService(entity?.gestor_service)) return false;
  }
  return true;
}
